import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo } from '../../utils';
import Base from '../Base';
import Icon from '../../components/common/Icon';
import './CompanyAwardLB.css';

const companyFallback = require('./../../assets/images/fallback/company.png');

/* ----------------------- podium slot ----------------------- */

const Slot = ({ company, rank, tone, awardsLabel }) => {
  const photo = company.photo_url || companyFallback;
  const crownIcon = rank === 1 ? 'crown' : 'medal';

  return (
    <Link to={`/public-company/${company.id}`} className={`cal-slot cal-slot--${tone}`}>
      <span className="cal-slot__crown">
        <Icon name={crownIcon} pack="sr" />
      </span>

      <div className="cal-slot__avatar-wrap">
        <div
          className="cal-slot__avatar"
          style={{ backgroundImage: `url(${photo})` }}
        />
        <span className="cal-slot__rank">{rank}</span>
      </div>

      <p className="cal-slot__name">{company.name}</p>

      <div className="cal-slot__count">
        {company.count}
        <span className="cal-slot__count-label">{awardsLabel}</span>
      </div>
    </Link>
  );
};

/* ----------------------- list row ----------------------- */

const Row = ({ company, rank, awardsLabel }) => {
  const photo = company.photo_url || companyFallback;

  return (
    <Link to={`/public-company/${company.id}`} className="cal-row">
      <div className="cal-row__rank">{rank}</div>
      <div
        className="cal-row__avatar"
        style={{ backgroundImage: `url(${photo})` }}
      />
      <div className="cal-row__body">
        <p className="cal-row__name">{company.name}</p>
        <p className="cal-row__sub">{awardsLabel}</p>
      </div>
      <div className="cal-row__count">
        <Icon name="medal" pack="sr" />
        {company.count}
      </div>
    </Link>
  );
};

/* ----------------------- main ----------------------- */

const CompanyAwardLB = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/company-award-lb', setData, setLoading, setError, []);
  }, []);

  const list = data?.leaderboard || [];
  const top3 = list.slice(0, 3);
  const rest = list.slice(3);
  const awardsLabel = t('awards');

  return (
    <Base activeNav="home">
      <div className="cal">
        {/* HEADER */}
        <div className="cal-header">
          <button
            className="cal-header__back"
            onClick={() => navigate(-1)}
            aria-label={t('back', { defaultValue: 'Back' })}
          >
            <Icon name="angle-left" />
          </button>

          <div className="cal-header__kicker">{t('leaderboard')}</div>
          <h1 className="cal-header__title">
            {data?.organizer_name || t('awards')}
          </h1>
        </div>

        {/* LOADING */}
        {loading && !data && (
          <>
            <div className="cal-skel-podium" />
            <div className="cal-skel-row" />
            <div className="cal-skel-row" />
            <div className="cal-skel-row" />
          </>
        )}

        {/* EMPTY */}
        {!loading && list.length === 0 && (
          <div className="cal-empty">{t('noLeaderboardFound')}</div>
        )}

        {/* PODIUM */}
        {top3.length > 0 && (
          <div className="cal-podium">
            <div className="cal-podium__kicker">
              <Icon name="trophy" pack="sr" /> {t('hallOfFame', { defaultValue: t('awards') })}
            </div>
            <div className="cal-podium__row">
              {top3[1] ? (
                <Slot company={top3[1]} rank={2} tone="silver" awardsLabel={awardsLabel} />
              ) : (
                <span />
              )}
              {top3[0] && (
                <Slot company={top3[0]} rank={1} tone="gold" awardsLabel={awardsLabel} />
              )}
              {top3[2] ? (
                <Slot company={top3[2]} rank={3} tone="bronze" awardsLabel={awardsLabel} />
              ) : (
                <span />
              )}
            </div>
          </div>
        )}

        {/* REST */}
        {rest.length > 0 && (
          <>
            {top3.length > 0 && <div className="cal-divider">{t('rank')}</div>}
            <div className="cal-list">
              {rest.map((company, i) => (
                <Row
                  key={company.id}
                  company={company}
                  rank={i + 4}
                  awardsLabel={awardsLabel}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Base>
  );
};

export default CompanyAwardLB;
